import React, { FormEvent, useCallback, useEffect, useState } from "react";
import GenericTable from "components/table";
import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import { UserRowAction } from "./UserRowAction";
import { ChevronDown } from "lucide-react";
import { Dialog } from "components/dialog";
import { useConfirmation } from "components/dialog/confirmation";
import { useACL } from "layouts/agent/agent-info/agent-acl/_context/AgentACLContext";
import ACLSelect from "../../../components/ACLSelect";
import { ACLEntry, ACLEntryPermission, ACLPayload } from "types";
import api from "api/axios";
import { useAgentInfo } from "layouts/agent/agent-info/_context/AgentInfoContext";
import { User } from "auth/AuthProvider";
import { fetchUsersCurrentWorkspaceAPI } from "api/axios";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";

const columnHelper = createColumnHelper<ACLEntry>();

function SearchTableUsersACL(props: { tableData: ACLEntry[] }) {
  const { t } = useTranslation();
  const { agentInfo } = useAgentInfo();
  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);

  const { confirm } = useConfirmation();

  // Dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { permissions, refetch } = useACL();
  const [addAclEntries, setAddAclEntries] = useState<ACLEntryPermission[]>(
    permissions.map((perm) => ({ permission: perm, access: "NOT_SET" }))
  );

  // Add state for users, loading, error, pagination
  const [users, setUsers] = useState<User[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [usersSkip, setUsersSkip] = useState(0);
  const USERS_TAKE = 20;

  // Fetch users
  const fetchUsersList = useCallback(
    async (reset = false) => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const data = await fetchUsersCurrentWorkspaceAPI({
          take: USERS_TAKE,
          skip: reset ? 0 : usersSkip,
        });
        setUsersTotal(data.total);
        setUsers((prev) => (reset ? data.items : [...prev, ...data.items]));
        if (reset) setUsersSkip(USERS_TAKE);
        else setUsersSkip((prev) => prev + USERS_TAKE);
      } catch (err) {
        setUsersError("Failed to fetch users");
        handleAxiosAndCaughtError(err);
      } finally {
        setUsersLoading(false);
      }
    },
    [usersSkip]
  );

  // Initial fetch
  useEffect(() => {
    if (isAddDialogOpen) fetchUsersList(true);
  }, [isAddDialogOpen, fetchUsersList]);

  const handleDeleteRow = async (id: string) => {
    const confirmed = await confirm({
      title: t("acl.deleteUser"),
      message: t("acl.deleteUserMessage"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      setDeletingRowId(id);
      try {
        await api.delete(`/agents/${agentInfo.id}/acl/${id}`);
        await refetch();
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setDeletingRowId(null);
      }
    }
  };

  const columns: ColumnDef<ACLEntry, any>[] = [
    columnHelper.display({
      id: "name",
      header: () => <p>{t("acl.userName")}</p>,
      cell: (info: any) => {
        const row = info.row.original;
        return (
          <div className="flex w-full items-center gap-[14px]">
            <div className="flex h-[60px] w-[60px] items-center justify-center rounded-full bg-blue-300">
              <img
                className="h-full w-full rounded-full"
                src={row.image}
                alt={row.subject_name}
              />
            </div>
            <p className="font-medium text-navy-700 dark:text-white">
              {row.subject_name}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => <p>{t("common.description")}</p>,
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()?.length > 0 ? info.getValue() : t("library.noDescriptionProvided")}
        </p>
      ),
    }),
    columnHelper.accessor("subject_id", {
      id: "actions",
      header: () => (
        <p className="p-4 text-left font-medium text-gray-900 dark:text-gray-100"></p>
      ),
      cell: (info) => (
        <UserRowAction
          info={info}
          deletingRowId={deletingRowId}
          handleDeleteRow={handleDeleteRow}
        />
      ),
    }),
  ];

  // Inject deletingRowId and handleDeleteRow into each row for the action cell
  const dataWithActions = props.tableData.map((row) => ({
    ...row,
    deletingRowId,
    handleDeleteRow,
  }));

  // Optionally, highlight the row being deleted
  const renderRowClassName = (row: any) => {
    const rowId = row.original.id;
    return deletingRowId === rowId ? "animate-pulse opacity-60" : "";
  };

  const handleAddUser = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedUserId) return;
    setIsSubmitting(true);
    try {
      const payload: ACLPayload = {
        subject_type: "USER",
        subject_id: selectedUserId,
        subject_name:
          selectedUser.name.length > 0 ? selectedUser.name : selectedUser.email,
        entries: addAclEntries,
      };
      await api.put(`/agents/${agentInfo.id}/acl`, payload);
      await refetch();

      setIsSubmitting(false);
      setIsAddDialogOpen(false);
      setSelectedUserId("");
      setIsDropdownOpen(false);
      setAddAclEntries(
        permissions.map((perm) => ({ permission: perm, access: "NOT_SET" }))
      );
    } catch (err) {
      setIsSubmitting(false);
      handleAxiosAndCaughtError(err);
    }
  };

  const selectedUser = users.find((user) => user.id === selectedUserId);

  return (
    <>
      <GenericTable<ACLEntry>
        data={dataWithActions}
        columns={columns}
        renderRowClassName={renderRowClassName}
        searchPlaceholder={t("acl.searchUsers")}
        onAdd={() => setIsAddDialogOpen(true)}
        debug={true}
        addLabel={t("acl.addUser")}
      />
      <Dialog
        header={t("acl.addUser")}
        isOpen={isAddDialogOpen}
        setIsOpen={setIsAddDialogOpen}
      >
        <form
          onSubmit={handleAddUser}
          className="flex min-w-[50vw] flex-col gap-6 md:min-w-[30vw]"
        >
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("acl.selectUser")}
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-2 text-left shadow-sm transition-colors hover:bg-gray-50 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-20 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
              >
                {selectedUser ? (
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-200 text-xs font-bold text-blue-700 sm:h-10 sm:w-10 sm:text-sm">
                      {selectedUser.name.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white sm:text-base">
                        {selectedUser.name}
                      </p>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {selectedUser.email}
                      </p>
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400">
                    {t("acl.chooseUser")}
                  </span>
                )}
                <ChevronDown
                  size={20}
                  className={`text-gray-400 transition-transform ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                  <div className="max-h-60 overflow-y-auto">
                    {usersLoading && users.length === 0 && (
                      <div className="flex items-center justify-center p-4 text-gray-500">
                        {t("common.loading")}
                      </div>
                    )}
                    {usersError && users.length === 0 && (
                      <div className="flex flex-col items-center p-4">
                        <span className="text-red-500">{usersError}</span>
                        <button
                          type="button"
                          className="mt-2 text-blue-500 underline"
                          onClick={() => fetchUsersList(true)}
                        >
                          {t("common.retry")}
                        </button>
                      </div>
                    )}
                    {users.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => {
                          setSelectedUserId(user.id);
                          setIsDropdownOpen(false);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none dark:hover:bg-gray-700 dark:focus:bg-gray-700 sm:gap-3 sm:px-4 sm:py-3"
                      >
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-200 text-xs font-bold text-blue-700 sm:h-10 sm:w-10 sm:text-sm">
                          {user.name.charAt(0)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900 dark:text-white sm:text-base">
                            {user.name}
                          </p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {user.email}
                          </p>
                        </div>
                      </button>
                    ))}
                    {usersError && users.length > 0 && (
                      <div className="flex flex-col items-center p-2">
                        <span className="text-xs text-red-500">
                          {usersError}
                        </span>
                        <button
                          type="button"
                          className="mt-1 text-xs text-blue-500 underline"
                          onClick={() => fetchUsersList(false)}
                        >
                          {t("common.retry")}
                        </button>
                      </div>
                    )}
                    {!usersLoading &&
                      users.length < usersTotal &&
                      !usersError && (
                        <button
                          type="button"
                          className="w-full p-2 text-center text-blue-500 hover:underline"
                          onClick={() => fetchUsersList(false)}
                        >
                          {t("acl.loadMore")}
                        </button>
                      )}
                    {usersLoading && users.length > 0 && (
                      <div className="flex items-center justify-center p-2 text-xs text-gray-400">
                        {t("acl.loadingMore")}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("acl.setPermissions")}
            </label>
            <ACLSelect
              permissions={permissions}
              value={addAclEntries}
              onChange={setAddAclEntries}
              loading={isSubmitting}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsAddDialogOpen(false);
                setSelectedUserId("");
                setIsDropdownOpen(false);
              }}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-20 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="flex-1 rounded-lg bg-brand-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSubmitting || !selectedUserId}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                  {t("acl.adding")}
                </div>
              ) : (
                t("acl.addUser")
              )}
            </button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

export default SearchTableUsersACL;
